import { createContext, useContext, useState, useEffect, useCallback } from "react";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { readActiveMunicipalityId, writeActiveMunicipalityId } from "@/lib/municipalityStorage";

export interface AddressLookupMetadata {
  provider: string;
  required_fields: string[];
  supported_fields?: string[];
  fuzzy_search_enabled?: boolean;
  fuzzy_search_provider?: string;
}

export interface Municipality {
  id: number;
  name: string;
  state?: string | null;
  classification: string;
  vector_db_name: string;
  created_at: string;
  updated_at: string;
  local_knowledge: string[];
  base_url: string | null;
  url_format: string | null;
  crest_url?: string | null;
  constituent_complaint?: boolean;
  widget_button_text?: string | null;
  widget_bot_name?: string | null;
  widget_name?: string | null;
  widget_primary_color?: string | null;
  widget_secondary_color?: string | null;
  address_lookup?: AddressLookupMetadata | null;
  /** True when at least one of the user's teams with this municipality has the manager Flipper */
  municipality_resource_links_access?: boolean;
  /** True when at least one of the user's teams with this municipality has File Viewer enabled */
  file_viewer_access?: boolean;
  /** True when this city is on a flagged team and the user has manage_documents there */
  document_management?: boolean;
  /** True when at least one of the user's teams with this municipality has the Documents Flipper */
  document_management_flagged?: boolean;
  /** True when this city is on a flagged team and the user has manage_users there */
  team_management?: boolean;
  /** True when at least one of the user's teams with this municipality has AI Partner enabled (ENG-1824) */
  ai_partner?: boolean;
  /** True when the user's grant workspace team for this city has Grant Radar enabled (ENG-2359) */
  grant_radar?: boolean;
}

interface MunicipalityContextType {
  municipalities: Municipality[];
  isLoading: boolean;
  /**
   * False until the stored or single-city selection has been applied.
   * List length is not enough: a multi-city load is one commit ahead of the
   * effect that restores the stored city.
   */
  selectionResolved: boolean;
  municipality?: Municipality;
  setMunicipalityId: (id: number) => void;
  getMunicipality: (id: number) => Municipality | undefined;
}

const MunicipalityContext = createContext<MunicipalityContextType | undefined>(undefined);

const fetchMunicipalities = async (): Promise<Municipality[]> => {
  const response = await fetch("/api/v1/municipalities");
  if (!response.ok) {
    throw new Error(`Failed to fetch municipalities: ${response.status}`);
  }
  return response.json();
};

export function MunicipalityProvider({ children }: { children: ReactNode }) {
  const { data: municipalities = [], isLoading } = useQuery({
    queryKey: ["municipalities"],
    queryFn: fetchMunicipalities,
    staleTime: 1000 * 60 * 5,
    refetchOnMount: true,
  });

  const [municipalityId, setMunicipalityIdState] = useState<number | undefined>();
  const [municipality, setMunicipality] = useState<Municipality | undefined>();
  const [selectionResolved, setSelectionResolved] = useState(false);

  const setMunicipalityId = useCallback((id: number) => {
    setMunicipalityIdState(id);
    writeActiveMunicipalityId(id);
  }, []);

  useEffect(() => {
    if (isLoading) {
      setSelectionResolved(false);
      return;
    }

    if (municipalityId) {
      setSelectionResolved(true);
      return;
    }

    if (municipalities.length === 0) {
      setSelectionResolved(true);
      return;
    }

    const storedId = readActiveMunicipalityId();
    const storedMunicipality = storedId ? municipalities.find((m) => m.id === storedId) : undefined;
    if (storedMunicipality) {
      setMunicipalityIdState(storedMunicipality.id);
      return;
    }

    if (municipalities.length === 1) {
      setMunicipalityId(municipalities[0].id);
      return;
    }

    setSelectionResolved(true);
  }, [isLoading, municipalities, municipalityId, setMunicipalityId]);

  useEffect(() => {
    if (municipalityId && municipalities.length > 0) {
      const foundMunicipality = municipalities.find((m) => m.id === municipalityId);
      setMunicipality(foundMunicipality);
    } else {
      setMunicipality(undefined);
    }
  }, [municipalityId, municipalities]);

  const getMunicipality = (id: number) => {
    return municipalities.find((municipality) => municipality.id === id);
  };

  return (
    <MunicipalityContext.Provider
      value={{
        municipalities,
        isLoading,
        selectionResolved,
        municipality,
        setMunicipalityId,
        getMunicipality,
      }}
    >
      {children}
    </MunicipalityContext.Provider>
  );
}

export function useMunicipalityContext() {
  const context = useContext(MunicipalityContext);
  if (context === undefined) {
    throw new Error("useMunicipalityContext must be used within a MunicipalityProvider");
  }
  return context;
}

// Keep the old function name for backward compatibility
export function useMunicipality() {
  return useMunicipalityContext();
}
